import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { AgencyTimesheetValueAccessEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { toNumber } from 'lodash';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

type AddNewMarginProps = {
  isOpen: boolean;
  toggle: () => void;
  accessType: AgencyTimesheetValueAccessEnum;
  defaultValues: AddNewMarginFormValues;
  minFrom: number;
  onSubmit: (values: AddNewMarginFormValues) => void;
  isSubmitting: boolean;
};

export type AddNewMarginFormValues = {
  from: string;
  to: string;
  margin: string;
};

const AddNewMarginModal: React.FC<AddNewMarginProps> = ({
  isOpen,
  toggle,
  accessType,
  defaultValues = {},
  minFrom = 0,
  onSubmit,
  isSubmitting = false,
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<AddNewMarginFormValues>();

  const onFinish: FormProps<AddNewMarginFormValues>['onFinish'] = (values) => {
    onSubmit?.(values);
  };

  const onFinishFailed: FormProps<AddNewMarginFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.trace('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.addNew']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        initialValues={defaultValues}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Form.Item
          label={messages['common.from']}
          name={'from'}
          rules={[
            {
              required: true,
            },
            {
              validator: async (_: any, value: any) => {
                if (value === '') return Promise.resolve();
                const fromNum = toNumber(value);
                const toVal = form.getFieldValue('to');
                if (toVal !== undefined && toVal !== '') {
                  const toNum = toNumber(toVal);
                  if (
                    !Number.isNaN(fromNum) &&
                    !Number.isNaN(toNum) &&
                    fromNum > toNum
                  ) {
                    return Promise.reject(
                      new Error('From cannot be greater than To'),
                    );
                  }
                }
                if (!Number.isNaN(fromNum) && fromNum < 0) {
                  return Promise.reject(
                    new Error('From must be greater than or equal to 0'),
                  );
                }
                if (
                  !Number.isNaN(fromNum) &&
                  minFrom > 0 &&
                  fromNum <= minFrom
                ) {
                  return Promise.reject(
                    new Error(
                      `From must be greater than previous To (= ${minFrom})`,
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <AppCurrencyInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={messages['common.to']}
          name={'to'}
          rules={[
            {
              required: true,
            },
            {
              validator: async (_: any, value: any) => {
                if (value === '') return Promise.resolve();
                const toNum = toNumber(value);
                const fromVal = form.getFieldValue('from');
                if (fromVal !== undefined && fromVal !== '') {
                  const fromNum = toNumber(fromVal);
                  if (
                    !Number.isNaN(fromNum) &&
                    !Number.isNaN(toNum) &&
                    toNum < fromNum
                  ) {
                    return Promise.reject(
                      new Error('To cannot be less than From'),
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <AppCurrencyInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={
            accessType === AgencyTimesheetValueAccessEnum.PERCENT
              ? `% ${messages['common.marginAmount']}`
              : messages['common.marginAmount']
          }
          name={'margin'}
          rules={[
            {
              required: true,
            },
          ]}
        >
          {accessType !== AgencyTimesheetValueAccessEnum.PERCENT ? (
            <AppCurrencyInput placeholder={messages['form.enter']} />
          ) : (
            <AppRateInput placeholder={messages['form.enter']} />
          )}
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default AddNewMarginModal;
